/**
 * InAppFeedbackListItem — a single admin users-feedback list row.
 *
 * Flutter source of truth: `InAppFeedbackListItem`
 * (`lib/presentation/screens/feedback/widgets/in_app_feedback_list_item.dart:24-91`).
 *
 * Layout (mirrors Flutter's `Row(avatar, Expanded(description + Created/Screenshots))`):
 *   - Leading user avatar — `CircleBorderAvatar` with
 *     `TextUtils.generateUserProfileImageUrl(userId)`, tappable → `onUserTap(userId)`
 *     (`in_app_feedback_list_item.dart:40-50`). Web parity: `CachedImage` +
 *     `UserUtils.generateUserProfileImageUrl`, sized `responsive.avatar.medium`
 *     (the established web token for Flutter's `profileImageMessagesSize = 50`,
 *     see `NotificationAvatar.tsx` / `InboxChatItem.tsx`).
 *   - Description — first 50 chars, single line, ellipsized
 *     (`in_app_feedback_list_item.dart:25-26,58-65`). See `description` note below.
 *   - `Created: ${formatDate(timestamp, true)}` subtitle. Web uses the
 *     locale-aware `useDateFormatters().formatDate` (Today/Yesterday/Tomorrow
 *     labels + localized "dd MMM[, HH:MM]"), which intentionally diverges from
 *     Flutter's fixed-English `DateUtils.getDateTime` "dd MMM[ yyyy] h:mm AM/PM".
 *   - `Screenshots: ${count|None}` subtitle, where `None` is used iff
 *     `screenshots == null` (`in_app_feedback_list_item.dart:27-29,77`).
 *   - Whole-row tap → `onTap(feedback)` (`in_app_feedback_list_item.dart:30-34`).
 *
 * Description truncation — Flutter parity note (`conventions.md` → "Handling
 * obvious Flutter bugs"): Flutter computes
 * `description.substring(0, min(50, description.length - 1))`, which caps at 50
 * but ALSO drops the final character of any string shorter than 51 chars (an
 * off-by-one `length - 1` bug). The web implements the CORRECT behaviour —
 * `description.slice(0, 50)` — so short descriptions render in full.
 * Commit footer: `Bug found on Flutter, web implemented correctly; follow-up
 * recommended to fix Flutter bug.`
 *
 * Pure renderer: navigation callbacks (`onTap`, `onUserTap`) come from the page
 * (Task 11). All copy via l10n, all colors/sizes via theme/responsive tokens.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { InAppFeedbackEntity } from '@/domain/entities/feedback/inAppFeedbackEntity';

const DESCRIPTION_MAX_CHARS = 50;

export interface InAppFeedbackListItemProps {
  feedback: InAppFeedbackEntity;
  /** Row tap → feedback details (`in_app_feedback_list_item.dart:30-34`). */
  onTap: (feedback: InAppFeedbackEntity) => void;
  /** Avatar tap → user details (`in_app_feedback_list_item.dart:40-43`). */
  onUserTap: (userId: string) => void;
}

export const InAppFeedbackListItem: React.FC<InAppFeedbackListItemProps> = ({
  feedback,
  onTap,
  onUserTap,
}) => {
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { formatDate } = useDateFormatters();
  const c = l10n.feedback;

  // Correct (non-Flutter-bug) truncation — first 50 chars, no off-by-one drop.
  const description = feedback.description.slice(0, DESCRIPTION_MAX_CHARS);

  // `None` iff screenshots == null, else the count (`in_app_feedback_list_item.dart:27-29`).
  const screenshotsCount =
    feedback.screenshots == null
      ? c.screenshotsNone
      : feedback.screenshots.length.toString();

  return (
    <div
      {...qaAttr({ id: `feedback-item-${feedback.id}` })}
      onClick={() => onTap(feedback)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        cursor: 'pointer',
      }}
    >
      {/* Leading user avatar → user details (tap stops row tap) */}
      <div
        {...qaAttr({ id: `feedback-item-avatar-${feedback.userId}` })}
        onClick={(e) => {
          e.stopPropagation();
          onUserTap(feedback.userId);
        }}
        style={{ flexShrink: 0, cursor: 'pointer', lineHeight: 0 }}
      >
        <CachedImage
          src={UserUtils.generateUserProfileImageUrl(feedback.userId)}
          alt=""
          style={{
            width: responsive.avatar.medium,
            height: responsive.avatar.medium,
            borderRadius: responsive.borderRadius.round,
            objectFit: 'cover',
          }}
        />
      </div>

      {/* Description + Created / Screenshots subtitles */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          paddingLeft: responsive.spacing.sm,
          paddingRight: responsive.spacing.sm,
        }}
      >
        <div
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.medium,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {description}
        </div>

        <div
          style={{
            marginTop: responsive.spacing.xs,
            color: theme.text.secondary,
            fontSize: responsive.font.sm,
          }}
        >
          <div>
            {c.createdPrefix}
            {formatDate(feedback.timestamp, true)}
          </div>
          <div>
            {c.screenshotsPrefix}
            {screenshotsCount}
          </div>
        </div>
      </div>
    </div>
  );
};
